/**
 * PipelineContextMenu — Right-click context menu for the pipeline canvas.
 *
 * Appears on right-click of empty canvas space. Shows all addable node
 * types grouped by category.
 */
import React from 'react';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from '@mui/material';
import { AttachMoney as CostIcon, Schedule as ComingSoonIcon } from '@mui/icons-material';
import { NODE_TYPE_REGISTRY, type AddNodeType } from './utils/nodeTypeRegistry';
import type { CanAddNodeResult } from './hooks/useNodeSelection';

// ── Props ────────────────────────────────────────────────────────────

interface PipelineContextMenuProps {
  /** Anchor position { x, y } from the right-click event, or null to hide. */
  position: { x: number; y: number } | null;
  onClose: () => void;
  onAddNode: (type: AddNodeType) => void;
  canAddNode: CanAddNodeResult;
}

// ── Grouping ─────────────────────────────────────────────────────────

const CATEGORY_ORDER: Array<{ key: string; label: string }> = [
  { key: 'transform', label: 'Transform' },
  { key: 'enrich', label: 'Enrich' },
  { key: 'quality', label: 'Quality' },
];

// ── Component ────────────────────────────────────────────────────────

const PipelineContextMenu: React.FC<PipelineContextMenuProps> = ({
  position,
  onClose,
  onAddNode,
  canAddNode,
}) => {
  const handleSelect = (type: AddNodeType) => {
    onClose();
    if (canAddNode.allowed) {
      onAddNode(type);
    }
  };

  return (
    <Menu
      open={position !== null}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position ? { top: position.y, left: position.x } : undefined}
      PaperProps={{
        sx: { minWidth: 220, maxWidth: 280, py: 0.5 },
      }}
    >
      {/* Guard message */}
      {!canAddNode.allowed && (
        <MenuItem disabled sx={{ py: 0.5 }}>
          <Typography variant="caption" color="warning.main">
            {canAddNode.reason}
          </Typography>
        </MenuItem>
      )}

      {/* Flatten categories — MUI Menu does not accept Fragment children */}
      {CATEGORY_ORDER.flatMap(({ key, label }, catIdx) => {
        const items = NODE_TYPE_REGISTRY.filter((d) => d.category === key);
        if (items.length === 0) return [];
        const result: React.ReactNode[] = [];
        if (catIdx > 0) result.push(<Divider key={`div-${key}`} sx={{ my: 0.5 }} />);
        result.push(
          <MenuItem key={`hdr-${key}`} disabled sx={{ py: 0.25, minHeight: 'auto' }}>
            <Typography variant="overline" fontSize={10} color="text.secondary">
              {label}
            </Typography>
          </MenuItem>,
        );
        items.forEach((item) => {
          const Icon = item.icon;
          const isComingSoon = item.comingSoon === true;
          result.push(
            <MenuItem
              key={item.type}
              disabled={!canAddNode.allowed || isComingSoon}
              onClick={() => !isComingSoon && handleSelect(item.type as AddNodeType)}
              sx={{ py: 0.75, opacity: isComingSoon ? 0.55 : 1 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 0.75,
                    bgcolor: alpha(item.color, isComingSoon ? 0.08 : 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 14, color: item.color }} />
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
              {isComingSoon && (
                <Typography variant="caption" sx={{ ml: 1, color: 'text.disabled', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Coming Soon
                </Typography>
              )}
              {!isComingSoon && item.costImplication && (
                <CostIcon sx={{ fontSize: 12, color: 'warning.main', ml: 1 }} />
              )}
            </MenuItem>,
          );
        });
        return result;
      })}
    </Menu>
  );
};

export default PipelineContextMenu;
